import { PROJECT_MODE } from '@utils/constants';

export const fileTypeMap = Object.freeze({
	jpeg: 'jpg',
	jpg: 'jpg',
	tif: 'tif',
	tiff: 'tif',
	laz: 'laz',
});

/**
 * Extracts the file extension from a file object's name.
 *
 * @param {File} file - The file object containing the name property.
 * @returns {string} The lowercase file extension (e.g., "jpg", "pdf").
 */
export const getFileType = file => {
	const fileType = file.name.split('.').pop().toLowerCase();
	return fileTypeMap[fileType] ?? fileType;
};

/**
 * Returns the image file types that count as project files for the given mode.
 * Single image mode only accepts .jpg files; orthophoto mode also accepts a .tif.
 *
 * @param {string} mode - The project mode, one of PROJECT_MODE.
 * @returns {string[]} The normalized image file types (see fileTypeMap).
 */
export const getImageFileTypes = mode =>
	mode === PROJECT_MODE.ORTHOPHOTO ? ['jpg', 'tif'] : ['jpg'];

/**
 * Checks whether the files contain a .laz file but no image files valid for the given mode.
 * A .laz file is only a companion to the image files; it never counts as a project file on its own.
 *
 * @param {File[]} files - The files to check.
 * @param {string} mode - The project mode, one of PROJECT_MODE.
 * @returns {boolean} True when a .laz file is present without any image files.
 */
export const hasLazWithoutImages = (files, mode) => {
	const types = files.map(getFileType);
	const imageTypes = getImageFileTypes(mode);
	return types.includes('laz') && !types.some(type => imageTypes.includes(type));
};

/**
 * Builds the message shown when a .laz file is present without image files.
 * Single image mode only accepts .jpg files, so .tif is only mentioned for orthophoto projects.
 *
 * @param {string} mode - The project mode, one of PROJECT_MODE.
 * @returns {string} The message telling the user which image files to add.
 */
export const getLazWithoutImagesMessage = mode => {
	const imageTypes = mode === PROJECT_MODE.ORTHOPHOTO ? '.tif or .jpg' : '.jpg';
	return `The .laz file needs corresponding ${imageTypes} image files. Add them to create the project.`;
};

/**
 * Calculates the sum of file sizes in an array of files.
 *
 * @param {Array} files The array of files.
 * @returns {number} The sum of file sizes in bytes.
 */
export const getTotalFilesSize = files =>
	files.reduce((accumulator, file) => accumulator + file.size, 0);

/**
 * Converts bytes to megabytes.
 *
 * @param {number} bytes The number of bytes to convert.
 * @returns {number} The converted value in megabytes.
 */
export const convertToMegabytes = bytes => bytes / 1024 / 1024;

/**
 * Linearly interpolates between two values.
 *
 * @param {number} start - The start value.
 * @param {number} end - The end value.
 * @param {number} position - The interpolation factor (0 to 1).
 * @returns {number} The interpolated value.
 */
const lerp = (start, end, position) => (1 - position) * start + position * end;

/**
 * Linearly interpolates between two points.
 *
 * @param {Object} point1 The first point with x and y coordinates.
 * @param {number} point1.x The x coordinate of the first point.
 * @param {number} point1.y The y coordinate of the first point.
 * @param {Object} point2 The second point with x and y coordinates.
 * @param {number} point2.x The x coordinate of the second point.
 * @param {number} point2.y The y coordinate of the second point.
 * @param {number} position The interpolation factor (0 to 1).
 * @returns {Object} The interpolated point with x and y coordinates.
 */
const lerpPoint = (point1, point2, position) => {
	const x = lerp(point1.x, point2.x, position);
	const y = lerp(point1.y, point2.y, position);

	return { x, y };
};

/**
 * Calculates the extent of a footprint polygon.
 *
 * @param {Array} footprint The footprint polygon to calculate the extent for.
 * @returns {Array|null} The extent of the footprint polygon as an array [minX, minY, maxX, maxY].
 */
export const getFootprintExtent = footprint => {
	const initialState = {
		minX: Infinity,
		minY: Infinity,
		maxX: -Infinity,
		maxY: -Infinity,
	};

	const { minX, minY, maxX, maxY } =
		footprint.flat().reduce((accumulator, { x, y }) => {
			if (x < accumulator.minX) accumulator.minX = x;
			if (x > accumulator.maxX) accumulator.maxX = x;
			if (y < accumulator.minY) accumulator.minY = y;
			if (y > accumulator.maxY) accumulator.maxY = y;
			return accumulator;
		}, initialState) || {};

	return [minX, minY, maxX, maxY];
};

/**
 * Smooths the corners of the given footprint / an array of points with x & y coordinates.
 *
 * @param {Array<{x: number, y: number}>} points The array of points representing the footprint.
 * @param {number} accuracy The accuracy of the smoothing, determines the number of interpolated points. (0 to 1)
 * @param {number} smoothingFactor The factor by which to smooth the corners. (0 to 1)
 * @returns {Array<{x: number, y: number}>} The array of points with smoothed corners.
 */
export const smoothFootprintCorners = (points, accuracy, smoothingFactor) => {
	let pointsCopy = [...points];
	pointsCopy.pop();

	const smoothedCornerPoints = pointsCopy.reduce((accumulator, currentPoint, index) => {
		const previousPoint = pointsCopy[index - 1] ?? pointsCopy[pointsCopy.length - 1];
		const nextPoint = pointsCopy[index + 1] ?? pointsCopy[0];

		const startCornerPoint = lerpPoint(currentPoint, previousPoint, smoothingFactor);
		const endCornerPoint = lerpPoint(currentPoint, nextPoint, smoothingFactor);

		for (let i = 0; i < 1; i += accuracy) {
			const a = lerpPoint(startCornerPoint, currentPoint, i);
			const b = lerpPoint(currentPoint, endCornerPoint, i);
			const smoothedCornerPoint = lerpPoint(a, b, i);

			accumulator.push(smoothedCornerPoint);
		}

		return accumulator;
	}, []);

	return [...smoothedCornerPoints, smoothedCornerPoints[0]];
};
